import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { Plus, Phone, Mail, MessageSquare, Calendar, CheckCircle2, Clock, User } from "lucide-react";
import { format } from "date-fns";

export const ActivitiesLog = () => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    activity_type: "call",
    subject: "",
    description: "",
    contact_id: "",
    deal_id: "",
    due_date: "",
  });
  const queryClient = useQueryClient();

  // Fetch activities with related data
  const { data: activities, isLoading } = useQuery({
    queryKey: ["crm-activities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crm_activities")
        .select(`
          *,
          crm_contacts (
            id,
            customers (name, email, company)
          ),
          crm_deals (id, title, value),
          profiles!crm_activities_created_by_fkey (
            id,
            first_name,
            last_name
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Fetch contacts for dropdown
  const { data: contacts } = useQuery({
    queryKey: ["crm-contacts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crm_contacts")
        .select("id, customers(name, email)");
      if (error) throw error;
      return data;
    },
  });

  // Fetch deals for dropdown
  const { data: deals } = useQuery({
    queryKey: ["crm-deals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crm_deals")
        .select("id, title, value");
      if (error) throw error;
      return data;
    },
  });

  // Realtime subscription
  useRealtimeSubscription("crm_activities", "crm-activities");

  // Create activity mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("crm_activities")
        .insert([{
          ...data,
          created_by: user.id,
          due_date: data.due_date || null,
          contact_id: data.contact_id || null,
          deal_id: data.deal_id || null,
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-activities"] });
      toast.success("Activity logged successfully");
      setIsCreateOpen(false);
      setFormData({
        activity_type: "call",
        subject: "",
        description: "",
        contact_id: "",
        deal_id: "",
        due_date: "",
      });
    },
    onError: (error) => {
      toast.error("Failed to create activity: " + error.message);
    },
  });

  // Complete activity mutation
  const completeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("crm_activities")
        .update({ completed_at: new Date().toISOString() })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-activities"] });
      toast.success("Activity marked as complete");
    },
    onError: (error) => {
      toast.error("Failed to update activity: " + error.message);
    },
  });

  const getActivityIcon = (type: string) => {
    const icons: Record<string, any> = {
      call: Phone,
      email: Mail,
      meeting: Calendar,
      note: MessageSquare,
      task: CheckCircle2,
    };
    const Icon = icons[type] || MessageSquare;
    return Icon;
  };

  const getActivityColor = (type: string) => {
    const colors: Record<string, string> = {
      call: "bg-blue-500/10 text-blue-500",
      email: "bg-purple-500/10 text-purple-500",
      meeting: "bg-green-500/10 text-green-500",
      note: "bg-yellow-500/10 text-yellow-500",
      task: "bg-red-500/10 text-red-500",
    };
    return colors[type] || "bg-muted";
  };

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading activities...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Log Activity
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Log New Activity</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Activity Type *</Label>
                  <Select
                    value={formData.activity_type}
                    onValueChange={(value) => setFormData({ ...formData, activity_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Call</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="meeting">Meeting</SelectItem>
                      <SelectItem value="note">Note</SelectItem>
                      <SelectItem value="task">Task</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label>Contact</Label>
                  <Select
                    value={formData.contact_id}
                    onValueChange={(value) => setFormData({ ...formData, contact_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select contact" />
                    </SelectTrigger>
                    <SelectContent>
                      {contacts?.map((contact: any) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.customers?.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Deal</Label>
                  <Select
                    value={formData.deal_id}
                    onValueChange={(value) => setFormData({ ...formData, deal_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select deal" />
                    </SelectTrigger>
                    <SelectContent>
                      {deals?.map((deal) => (
                        <SelectItem key={deal.id} value={deal.id}>
                          {deal.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label>Due Date</Label>
                  <Input
                    type="datetime-local"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label>Subject *</Label>
                <Input
                  placeholder="e.g., Follow-up call"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                />
              </div>

              <div className="grid gap-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Add details..."
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <Button onClick={() => createMutation.mutate(formData)} disabled={!formData.subject}>
                Log Activity
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {activities && activities.length > 0 ? (
          activities.map((activity: any) => {
            const Icon = getActivityIcon(activity.activity_type);
            const contact = activity.crm_contacts?.customers;
            const deal = activity.crm_deals;
            const creator = activity.profiles;
            const isCompleted = !!activity.completed_at;
            const isOverdue = activity.due_date && new Date(activity.due_date) < new Date() && !isCompleted;

            return (
              <Card key={activity.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`p-3 rounded-full ${getActivityColor(activity.activity_type)}`}>
                      <Icon className="h-5 w-5" />
                    </div>

                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{activity.subject}</h4>
                        {isCompleted && (
                          <Badge variant="outline" className="bg-success/10 text-success border-success/20">
                            Completed
                          </Badge>
                        )}
                        {isOverdue && (
                          <Badge variant="destructive">Overdue</Badge>
                        )}
                      </div>

                      {activity.description && (
                        <p className="text-sm text-muted-foreground">{activity.description}</p>
                      )}

                      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                        {contact && (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {contact.name}
                          </div>
                        )}
                        {deal && (
                          <div>Deal: {deal.title}</div>
                        )}
                        {activity.due_date && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Due: {format(new Date(activity.due_date), "MMM d, yyyy h:mm a")}
                          </div>
                        )}
                      </div>

                      <div className="text-xs text-muted-foreground">
                        Created {format(new Date(activity.created_at), "MMM d, yyyy")}
                        {creator && ` by ${creator.first_name} ${creator.last_name}`}
                      </div>
                    </div>
                  </div>

                  {!isCompleted && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => completeMutation.mutate(activity.id)}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Complete
                    </Button>
                  )}
                </div>
              </Card>
            );
          })
        ) : (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">No activities logged yet. Start tracking your interactions!</p>
          </Card>
        )}
      </div>
    </div>
  );
};